/* eslint-disable react/jsx-pascal-case */
import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import NavBar from "../../../components/navbar/navbar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Edit, Delete } from "@mui/icons-material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useLocation, useNavigate } from "react-router-dom";
import { ActionTypes, SquareResponse, SquareRow, SquareState } from "./types";
import { API_URL, sentActionLog, verify } from "../../../utils/common";
import swal from "sweetalert";
import { ModalContainer } from "../styles";
import { SquareReducer } from "./reducer";
import { catalog } from "../../../models";
import { Error } from "../offers/types";
import nestedItemsReducer from "../../../components/selectNestedList/reducer";
import { NestedListState } from "../../../components/selectNestedList/types";
import SelectNestedList from "../../../components/selectNestedList/selectNestedList";

const initialState: SquareState = {
  zipCode: "",
  squareId: 0,
  squareLabel: "",
  offerClassificationId: 0,
};

const selectedInitialState: NestedListState = {
  first: [],
  second: [],
  third: [],
};

const Squares: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = React.useState<SquareRow[]>([]);
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoadingList, setIsloadingList] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);
  const [modalTitle, setModalTitle] = React.useState<string>("");
  const [state, dispatch] = React.useReducer(SquareReducer, initialState);
  const [selectedState, selectedDispatch] = React.useReducer(
    nestedItemsReducer,
    selectedInitialState
  );
  const [classifications, setClassifications] = React.useState<catalog[]>([]);
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const columns = React.useMemo<MRT_ColumnDef<SquareRow>[]>(
    () => [
      {
        accessorKey: "zipCode",
        header: "Código Postal",
      },
      {
        accessorKey: "square.name",
        header: "Nombre de Plaza",
      },
      {
        accessorKey: "classification.name",
        header: "Clasificación",
      },
      {
        accessorKey: "square.type.name",
        header: "Tipo",
      },
      {
        accessorKey: "square.brand.name",
        header: "Marca",
      },
      {
        accessorKey: "square.zone.region.name",
        header: "Region",
      },
      {
        accessorKey: "square.zone.state.name",
        header: "Estado",
      },
      {
        accessorKey: "square.zone.hub.name",
        header: "Hub",
      },
    ],
    []
  );
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const getSquares = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      page: pagination.pageIndex.toString(),
    });
    fetch(`${API_URL}/plazas/codigos-postales?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: SquareResponse) => {
        setIsLoading(false);
        setData(response.content);
        setRowCount(response.totalElements);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [pagination.pageIndex]);

  const getClassifications = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/clasificaciones`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setClassifications(response);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getStates = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/estados`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setIsloadingList(false);
        setIsLoading(false);
        selectedDispatch({ type: "SET_FIRST", payload: response });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getHubsByState = (idState: number) => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      id_estado: idState.toString(),
    });
    fetch(`${API_URL}/hubs?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        selectedDispatch({ type: "SET_SECOND", payload: response });
        setIsloadingList(false);
      })
      .catch((err) => console.error(err));
  };

  const getSquareByHub = (idHub: number) => {
    const token = localStorage.getItem("jwt");
    const params = new URLSearchParams({
      id_hub: idHub.toString(),
    });
    fetch(`${API_URL}/plazas?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        selectedDispatch({ type: "SET_THIRD", payload: response });
        setIsloadingList(false);
      })
      .catch((err) => console.error(err));
  };

  const createSquare = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/plazas/codigos-postales`, {
      method: "POST",
      body: JSON.stringify(state),
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: SquareRow) => {
        if (response.id) {
          swal({
            title: "Guardado!",
            text: "Se ha guardado correctamente! :)",
            icon: "success",
          });
          setOpen(false);
          setModalTitle("");
          dispatch({ type: "SET_STATE", payload: initialState });
          getSquares();
          sentActionLog('Create_Square', `Creo nuevo codigo postal en plaza con id: ${response.id}`);
        } else if( response.error){
          swal({
            title: "Error!",
            text: response.error,
            icon: "error",
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const editSquare = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/plazas/codigos-postales/${state.id}`, {
      method: "PUT",
      body: JSON.stringify(state),
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: SquareRow) => {
        if (response.id) {
          swal({
            title: "Guardado!",
            text: "Se ha guardado correctamente! :)",
            icon: "success",
          });
          setOpen(false);
          setModalTitle("");
          dispatch({ type: "SET_STATE", payload: initialState });
          getSquares();
          sentActionLog('Edit_Square', `Edito un codigo postal en plaza con id: ${response.id}`);
        } else if( response.error){
          swal({
            title: "Error!",
            text: response.error,
            icon: "error",
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const validateFields = () => {
    let isValid = true;
    const keys = Object.keys(state) as Array<keyof SquareState>;
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] === "zipCode") {
        if (state[keys[i]] === "") {
          setError({
            field: "zipCode",
            message: "Código Postal no puede estar vacío",
          });
          isValid = false;
          break;
        }
        if (typeof state[keys[i]] === "string" ){
          if((state[keys[i]] as string).length > 5){
            setError({
              field: "zipCode",
              message: "Código Postal no puede tener mas de 5 caracteres",
            });
            isValid = false;
            break;
          }
        }
      } else if (keys[i] === "squareId") {
        if (state[keys[i]] === 0) {
          setError({
            field: "squareId",
            message: "Plaza no puede estar vacia",
          });
          isValid = false;
          break;
        }
      } else if (keys[i] === "offerClassificationId") {
        if (state[keys[i]] === 0) {
          setError({
            field: "offerClassificationId",
            message: "Clasificación de oferta no puede estar vacio",
          });
          isValid = false;
          break;
        }
      } else {
        setError(undefined);
      }
    }
    return isValid;
  };

  React.useEffect(() => {
    getSquares();
    sentActionLog('Visit_Squares', 'Entro en la vista Plazas');
  }, [getSquares]);

  React.useEffect(() => {
    (async () => {
      if (!["/", "/login"].includes(location.pathname)) {
        const currentToken = localStorage.getItem("jwt");
        if (currentToken) {
          const isValid = await verify(currentToken);
          if (!isValid) {
            navigate("/");
          } else {
            const permissions = JSON.parse(
              localStorage.getItem("user") || "{}"
            );
            if (!permissions?.administrationAccess) {
              navigate("/Home");
            }
          }
        } else {
          navigate("/");
        }
      }
    })();
  }, [location.pathname, navigate]);

  const handleEdit = (row: SquareRow) => {
    const state = {
      id: row.id,
      zipCode: row.zipCode,
      squareId: row.square.id,
      squareLabel: row.square.name,
      offerClassificationId: row.classification.id,
    };
    dispatch({ type: "SET_STATE", payload: state });
    handleOpen("Editar");
  };

  const handleDelete = (row: SquareRow) => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/plazas/codigos-postales/${row.id}`, {
      method: "DELETE",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.ok)
      .then((response: boolean) => {
        if (response) {
          swal({
            title: "Eliminado!",
            text: "Se ha eliminado correctamente! :)",
            icon: "success",
          });
          getSquares();
          sentActionLog('Delete_Square', `Elimino un codigo postal en plaza con id: ${row.id}`);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleOpen = (title: string) => {
    getClassifications();
    getStates();
    setModalTitle(title);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setModalTitle("");
    dispatch({ type: "SET_STATE", payload: initialState });
  };

  const handleSelectedNestedChange = (
    value: number,
    level: "first" | "second" | "third"
  ) => {
    switch (level) {
      case "first":
        setIsloadingList(true);
        getHubsByState(value);
        break;
      case "second":
        setIsloadingList(true);
        getSquareByHub(value);
        break;
      case "third":
        dispatch({ type: "SET_SQUARE", payload: value });
        break;
      default:
        console.error("Error: level not found");
    }
  };

  const handleInputChange = (value: any, type: ActionTypes) => {
    dispatch({ type: type, payload: value });
  };

  const handleSave = () => {
    if (modalTitle) {
      if (validateFields()) {
        if (modalTitle === "Crear") {
          createSquare();
        } else if (modalTitle === "Editar") {
          editSquare();
        }
      }
    }
  };

  const table = useMaterialReactTable({
    columns,
    data,
    enableFilters: false,
    enableHiding: false,
    enableSorting: false,
    enableColumnActions: false,
    enableRowActions: true,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount,
    positionActionsColumn: "last",
    paginationDisplayMode: 'pages',
    renderToolbarInternalActions: ({ table }) => (
      <>
        <IconButton onClick={() => handleOpen("Crear")}>
          <AddIcon />
        </IconButton>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton color="primary" onClick={() => handleEdit(row.original)}>
          <Edit />
        </IconButton>
        <IconButton color="error" onClick={() => handleDelete(row.original)}>
          <Delete />
        </IconButton>
      </Box>
    ),
    state: {
      isLoading,
      pagination,
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    }
  });

  const renderItems = (catalog: catalog[]) => {
    return catalog.map((item) => (
      <MenuItem value={item.value}>{item.label}</MenuItem>
    ));
  };

  return (
    <Box width={"100%"} mb={8}>
      <NavBar />
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <MaterialReactTable table={table} />
        </CardContent>
      </Card>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Create-Edit-Square"
      >
        <ModalContainer theme={theme}>
          <Box display={"flex"} flexDirection="row-reverse">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={4} pt={0} maxHeight={"380px"} overflow={"auto"}>
            <Typography mb={2} variant="h5" fontWeight="bold">
              {modalTitle} Plaza
            </Typography>
            <FormControl fullWidth>
              <TextField
                error={error?.field === "zipCode"}
                color="secondary"
                label="Código Postal"
                variant="outlined"
                value={state.zipCode}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputChange(event.target.value, "SET_ZIPCODE");
                }}
              />
              {error?.field === "zipCode" && (
                <FormHelperText>{error.message}</FormHelperText>
              )}
            </FormControl>
            <FormControl fullWidth error={error?.field === "squareId"}>
              <SelectNestedList
                placeholder="Selecciona Plaza"
                defaultValue={state.squareLabel}
                items={selectedState}
                onChangeItem={handleSelectedNestedChange}
                isLoading={isLoadingList}
                showAdortments={false}
                isInForm={true}
              />
              {error?.field === "squareId" && (
                <FormHelperText>{error.message}</FormHelperText>
              )}
            </FormControl>
            <FormControl
              fullWidth
              error={error?.field === "offerClassificationId"}
            >
              <InputLabel color="secondary" id="classification">
                Clasificación
              </InputLabel>
              <Select
                color="secondary"
                labelId="classification"
                value={state.offerClassificationId}
                label="Clasificación"
                onChange={(e) =>
                  handleInputChange(e.target.value, "SET_OFFERCLASSIFICATION")
                }
              >
                {renderItems(classifications)}
              </Select>
              {error?.field === "classificationId" && (
                <FormHelperText>{error.message}</FormHelperText>
              )}
            </FormControl>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              Guardar
            </Button>
          </Box>
        </ModalContainer>
      </Modal>
    </Box>
  );
};

export default Squares;
